import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { FiX, FiChevronRight, FiCheckCircle } from "react-icons/fi";
import ProgressBar from "../../atoms/ProgressBar/ProgressBar";
import OnboardingCloseModal from "../../molecules/OnboardingCloseModal/OnboardingCloseModal";
import useOnboarding from "../../hooks/useOnboarding";
import { ONBOARDING_ROUTE } from "../../config/onboardingSteps";
import "./OnboardingSidebarCard.scss";

/**
 * Organism: card "Primeros pasos" del sidebar.
 *
 * Resume el progreso del onboarding y lleva al checklist. Se auto-oculta
 * cuando no aplica (no admin o descartada). Comparte estado con el checklist
 * a través del store, así que ambos van siempre sincronizados.
 */
const OnboardingSidebarCard = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const onboarding = useOnboarding();
	const { completedCount, total, isComplete, sidebarVisible, dismiss } =
		onboarding;
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [closing, setClosing] = useState(false);

	if (!sidebarVisible && !confirmOpen) return null;

	const pct = total > 0 ? (completedCount / total) * 100 : 0;

	const handleContinue = () => {
		history.push(ONBOARDING_ROUTE);
	};

	const handleDismissClick = (e) => {
		e.stopPropagation();
		setConfirmOpen(true);
	};

	const handleConfirmDismiss = async () => {
		setClosing(true);
		const ok = await dismiss();
		setClosing(false);
		setConfirmOpen(false);
		if (!ok) {
			toastr.error(t("common.error"), t("common.errorGeneric"));
		}
	};

	const handleCancelDismiss = () => {
		if (closing) return;
		setConfirmOpen(false);
	};

	return (
		<>
			{sidebarVisible && (
				<div
					className={`onboarding-card ${isComplete ? "is-complete" : ""}`}
					role="region"
					aria-label={t("onboarding.sidebar.title")}
				>
					<div className="onboarding-card__top">
						<span className="onboarding-card__title">
							{isComplete && (
								<FiCheckCircle className="onboarding-card__title-icon" />
							)}
							{t("onboarding.sidebar.title")}
						</span>
						<button
							type="button"
							className="onboarding-card__close btn-transparent"
							onClick={handleDismissClick}
							aria-label={t("onboarding.closeOnboarding")}
						>
							<FiX />
						</button>
					</div>

					<span className="onboarding-card__count">
						{t("onboarding.completedOf", {
							completed: completedCount,
							total,
						})}
					</span>

					<ProgressBar
						value={pct}
						size="sm"
						tone={isComplete ? "success" : "primary"}
						ariaLabel={t("onboarding.progress")}
					/>

					<button
						type="button"
						className="onboarding-card__cta btn btn-outline-primary"
						onClick={handleContinue}
					>
						<span>
							{isComplete
								? t("onboarding.sidebar.review")
								: t("onboarding.sidebar.continue")}
						</span>
						<FiChevronRight />
					</button>
				</div>
			)}

			<OnboardingCloseModal
				isOpen={confirmOpen}
				variant={isComplete ? "complete" : "incomplete"}
				total={total}
				onCancel={handleCancelDismiss}
				onConfirm={handleConfirmDismiss}
				isLoading={closing}
			/>
		</>
	);
};

export default OnboardingSidebarCard;
